<script setup>
// @ts-check

import ActionMenu from '@/components/actions/ActionMenu.vue'
import ReferenceDatatableMobileRow from '@/components/references/ReferenceDatatableMobileRow.vue'
import ReferenceDatatableRow from '@/components/references/ReferenceDatatableRow.vue'
import ReferenceDate from '@/components/references/ReferenceDate.vue'
import ReferenceStatusBadge from '@/components/references/ReferenceStatusBadge.vue'
import ReferenceStatusText from '@/components/references/ReferenceStatusText.vue'
import ServerDatatable from '@/components/tables/ServerDatatable.vue'
import { pickTruthy } from '@/helpers/functional'
import * as _ from 'lodash-es'
import { computed, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

const props = defineProps({
  useMobile: {
    type: Boolean
  },
  selected: {
    type: Number,
    default: null
  },
  headers: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['select'])

defineOptions({
  name: 'ReferenceDatatable',
  inheritAttrs: false
})

/** @type {import('vue').Ref<InstanceType<typeof ServerDatatable> | null>} */
const datatable = useTemplateRef('datatable')

const { t } = useI18n()

const store = useStore()

const isMobile = computed(() => props.useMobile || !store.getters['app/isMd'])

const defaultHeaders = computed(() => ({
  title: { title: t('general.title'), key: 'title' },
  date: { title: t('general.date'), key: 'date', width: '8rem' },
  status: { title: t('general.status'), key: 'status' },
  amount: { title: t('general.amount'), key: 'amount', width: '10rem' },
  actions: {
    title: t('general.menu'),
    key: 'actions',
    width: '4rem'
  }
}))

const combinedHeaders = computed(() =>
  Object.values(_.merge(defaultHeaders.value, props.headers))
)

defineExpose({
  refresh: () => datatable.value?.refresh()
})
</script>

<template>
  <ServerDatatable
    ref="datatable"
    disable-sort
    v-bind="$attrs"
    :headers="combinedHeaders"
  >
    <template v-if="isMobile" #headers />

    <template #header.actions="{ column }">
      <span class="float-right">{{ column.title }}</span>
    </template>

    <template #item="{ item: reference }">
      <tr
        :class="{ selected: selected === reference.id }"
        class="server-datatable-row cursor-pointer"
        @click="emit('select', reference)"
      >
        <component
          :is="isMobile ? ReferenceDatatableMobileRow : ReferenceDatatableRow"
          :item="reference"
        >
          <template #date>
            <ReferenceDate :reference />
          </template>
          <template #description>
            <slot name="description" :description="reference.description" />
          </template>
          <template #status>
            <slot name="status" :reference>
              <ReferenceStatusText :reference />
            </slot>
          </template>
          <template #badge>
            <slot name="badge" :reference>
              <ReferenceStatusBadge :reference />
            </slot>
          </template>
          <template #actions>
            <ActionMenu
              v-if="pickTruthy(reference.actions).length"
              :options="{ labelPath: `references.${reference.type}.actions` }"
              :actionable="reference"
              v-bind="_.omit($attrs, ['endpoint', 'refetch', 'loading'])"
            />
          </template>
        </component>
      </tr>
    </template>
  </ServerDatatable>
</template>

<style lang="scss" scoped>
@reference '../../styles/main.css';

:deep(table) {
  @apply border-spacing-y-3!;

  tbody {
    td:first-of-type {
      @apply rounded-l-md! border-l-8 border-solid border-transparent;
    }

    tr:has(.status-badge-primary) {
      &.selected {
        @apply outline-primary-500;
      }

      td:first-of-type {
        @apply border-l-primary-500;
      }
    }

    tr:has(.status-badge-info) {
      &.selected {
        @apply outline-blue-500;
      }

      td:first-of-type {
        @apply border-l-blue-500;
      }
    }

    tr:has(.status-badge-danger) {
      &.selected {
        @apply outline-red-500;
      }

      td:first-of-type {
        @apply border-l-red-500;
      }
    }

    tr:has(.status-badge-success) {
      &.selected {
        @apply outline-green-500;
      }

      td:first-of-type {
        @apply border-l-green-500;
      }
    }
  }
}

.server-datatable-row {
  @apply shadow-md;
  @apply hover:bg-secondary-50;
}
</style>
